import React from 'react'

import { Box, Button, InputCheckBox, Text } from '@factorialco/deprecated-design-system'
import {
  CreateSignedDocumentTermDocument,
  ExpensesRootPageDocument,
  ExpensesSettingsPageDocument,
  ItalyLegalEntityTermsDocument,
  RepresentativeDetailsDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import useLegalEntities from 'modules/expenses/hooks/useLegalEntities'
import {
  SignableOrganizationPerCountry,
  useTaxAgencyHomologations,
} from 'modules/expenses/hooks/useTaxAgencyHomologations'

const useCreateSignedDocumentTerms = () => {
  const [createSignedDocumentTerms, { loading }] = useMutation(CreateSignedDocumentTermDocument, {
    extractorFn: (data) => data.expensesMutations.createExpensesSignedDocumentsTerm,
    refetchQueries: [
      ExpensesRootPageDocument,
      ExpensesSettingsPageDocument,
      ItalyLegalEntityTermsDocument,
    ],
  })

  return {
    loading,
    createSignedDocumentTerms: (
      legalEntityId: number,
      signableOrganization: SignableOrganizationPerCountry,
      representativeDetails?: {
        name?: string
        surname?: string
        identifier?: string
      }
    ) =>
      createSignedDocumentTerms({
        variables: {
          legalEntityId,
          organization: signableOrganization,
          representativeName: representativeDetails?.name,
          representativeSurname: representativeDetails?.surname,
          representativeIdentifier: representativeDetails?.identifier,
        },
      }),
  }
}

interface TermsModalForm {
  name: string
  surname: string
  identifier: string
}
interface TermsModalProps {
  onClose: () => void
  title: string
  description: string
  termsLink: string
  organization: SignableOrganizationPerCountry
  legalEntityId?: number
}

const TermsModal = ({
  onClose,
  termsLink,
  organization,
  title,
  description,
  legalEntityId,
}: TermsModalProps) => {
  const [acceptedTerms, setAcceptedTerms] = React.useState(false)

  const { createSignedDocumentTerms, loading: submitting } = useCreateSignedDocumentTerms()

  const { mainLegalEntity, legalEntities } = useLegalEntities()
  const { organizations: taxAgencyOrgs } = useTaxAgencyHomologations(legalEntities)
  const { legalEntityIds } = taxAgencyOrgs[organization]

  const isADE = organization === SignableOrganizationPerCountry.it

  // For ES/FR we use the main legal entity, or fallback to any that has the homologation available
  const defaultLegalEntity =
    mainLegalEntity && legalEntityIds.includes(mainLegalEntity.id)
      ? mainLegalEntity.id
      : legalEntityIds[0]
  // For IT we use the selected legal entity ID instead
  const signingLegalEntity = isADE ? legalEntityId : defaultLegalEntity

  const { data } = useQuery(RepresentativeDetailsDocument, {
    variables: { legalEntityId: Number(signingLegalEntity) },
    skip: !signingLegalEntity,
  })
  const representativeData = data?.companies?.legalEntitiesConnection?.nodes?.[0]
  const employeeData = data?.apiCore?.currentsConnection?.edges?.[0]?.node?.employee

  const getDefaultValues = () => {
    if (representativeData?.representativeName) {
      return {
        name: representativeData?.representativeName || '',
        surname: representativeData?.representativeFirstSurname || '',
        identifier: representativeData?.representativeIdentifier || '',
      }
    } else {
      return {
        name: employeeData?.firstName || '',
        surname: employeeData?.lastName || '',
        identifier: employeeData?.identifier || '',
      }
    }
  }
  const form = useForm<TermsModalForm>({
    defaultValues: getDefaultValues(),
    enableReinitialize: true,
    validateOnChange: true,
    validate: (formData) => {
      const errors: FormErrors<TermsModalForm> = {}

      if (isADE) {
        if (!formData.name.trim()) {
          errors.name = i18n.t('common.validations.empty')
        }
        if (!formData.surname.trim()) {
          errors.surname = i18n.t('common.validations.empty')
        }
        if (!formData.identifier.trim()) {
          errors.identifier = i18n.t('common.validations.empty')
        } else {
          // Validate Italian fiscal code format: LLLLLL99L99L999L
          // LLLLLL: 6 letters (surname + first name consonants)
          // 99: 2 digits (year)
          // L: 1 letter (month)
          // 99: 2 digits (day)
          // L999: 4 alphanumeric characters (place of birth)
          // L: 1 letter (check character)
          const fiscalCodeRegex = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i
          if (!fiscalCodeRegex.test(formData.identifier)) {
            errors.identifier = i18n.t(
              'expenses.settings.signed_invoices.italy.representative_form.identifier_validation'
            )
          }
        }
      }
      return errors
    },
    onSubmit: async (values) => {
      if (!signingLegalEntity) return { success: false, errors: [] }

      if (isADE) {
        const representativeDetails = {
          name: values.name,
          surname: values.surname,
          identifier: values.identifier,
        }
        await createSignedDocumentTerms(signingLegalEntity, organization, representativeDetails)
      } else {
        await createSignedDocumentTerms(signingLegalEntity, organization)
      }
      onClose()
      return { success: true, data: values }
    },
  })

  return (
    <Modal
      illustration="document-folder-board-edit"
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            disabled={!acceptedTerms || submitting || Object.keys(form.errors).length > 0}
            submit
          >
            {i18n.t('common.accept')}
          </Button>
        </>
      }
    >
      {isADE && (
        <Box flexDirection="column" gap="s12" paddingTop="s8" paddingBottom="s24">
          <Text.Normal weight="medium">
            {i18n.t('expenses.settings.signed_invoices.italy.representative_form.title')}
          </Text.Normal>

          <Box flexDirection="row" gap="s12">
            <TextField
              mandatory
              label={i18n.t('expenses.settings.signed_invoices.italy.representative_form.name')}
              value={form.values.name}
              onChange={(value) => {
                form.setFieldValue('name', value)
                form.setFieldTouched('name', true)
              }}
              placeholder={i18n.t(
                'expenses.settings.signed_invoices.italy.representative_form.name_placeholder'
              )}
              error={form.errors.name}
            />
            <TextField
              mandatory
              label={i18n.t('expenses.settings.signed_invoices.italy.representative_form.surname')}
              value={form.values.surname}
              onChange={(value) => {
                form.setFieldValue('surname', value)
                form.setFieldTouched('surname', true)
              }}
              placeholder={i18n.t(
                'expenses.settings.signed_invoices.italy.representative_form.surname_placeholder'
              )}
              error={form.errors.surname}
            />
          </Box>

          <TextField
            mandatory
            label={i18n.t('expenses.settings.signed_invoices.italy.representative_form.identifier')}
            value={form.values.identifier}
            onChange={(value) => {
              form.setFieldValue('identifier', value)
              form.setFieldTouched('identifier', true)
            }}
            placeholder={i18n.t(
              'expenses.settings.signed_invoices.italy.representative_form.identifier_placeholder'
            )}
            error={form.errors.identifier}
          />
        </Box>
      )}
      <Box flexDirection="row" gap="s8" alignItems="center">
        <InputCheckBox
          onClick={() => {
            setAcceptedTerms(!acceptedTerms)
          }}
          checked={acceptedTerms}
        />
        <Text.Normal weight="medium">
          {i18n.tView('expenses.settings.signed_invoices.terms.checkbox_label', {
            terms_link: (
              <a
                href={termsLink}
                rel="nofollow noopener noreferrer"
                target="_blank"
                style={{
                  outline: 'none',
                  textDecoration: 'none',
                  color: '#07A2AD',
                }}
              >
                {i18n.t('expenses.settings.signed_invoices.terms.terms_and_conditions')}
              </a>
            ),
          })}
        </Text.Normal>
      </Box>
    </Modal>
  )
}

export default TermsModal
